<!-- components/Editor.svelte -->
<script>
    import logger from "../utils/logger";
    import { createEventDispatcher, onMount, onDestroy, tick } from "svelte";
    import EditorContent from "./EditorContent.svelte";
    import BlockTimestamp from "./BlockTimestamp.svelte";
    import { useSelection } from "../stores/selectionStore";
    import { useNavigation } from "../stores/navigationStore";

    export let block;
    export let autoFocus = false;
    export let isFirstBlock = false;

    const dispatch = createEventDispatcher();
    let editor;
    let contentComponent;
    let activeHashtags = [];

    const { saveSelection, restoreSelection } = useSelection();
    const { handleKeyNavigation } = useNavigation();

    // Function to sync block content with the parent component
    function handleContentUpdate(event) {
        const { html, json, type, hashtags } = event.detail;

        // Update active hashtags if they were provided
        if (hashtags) {
            activeHashtags = hashtags;
        }

        dispatch("update", {
            id: block.id,
            content: html,
            contentJSON: json,
            type: type || block.type || "paragraph",
            hashtags: activeHashtags,
        });
    }

    // Handle block type changes from the editor
    function handleBlockTypeChanged(event) {
        const { type } = event.detail;
        dispatch("update", {
            id: block.id,
            type,
        });
    }

    // Handle hashtag clicks
    function handleHashtagClick(event) {
        const { hashtag } = event.detail;
        logger.debug("Hashtag clicked in Editor component:", hashtag);

        // Dispatch an event to the parent component
        dispatch("hashtagclick", {
            blockId: block.id,
            hashtag,
        });
    }

    // Handle key events
    function handleKeyDown(event) {
        const result = handleKeyNavigation({
            event,
            editor,
            blockId: block.id,
            isFirstBlock,
            isEmptyBlock: contentComponent?.isEmpty() || false,
        });

        if (result.action === "createBlock") {
            dispatch("createBlock", {
                index: result.index,
                focus: "new",
            });
        } else if (result.action === "deleteBlock") {
            dispatch("deleteBlock", { id: block.id });
        } else if (result.action === "createBlockAtTop") {
            dispatch("createBlockAtTop", { focus: "new" });
        }
    }

    // Expose a method to focus this editor
    export function focusEditor(position = "end") {
        return contentComponent?.focusEditor(position) || false;
    }

    // Apply different block styles based on block type
    $: blockStyle = getBlockStyle(block.type);

    function getBlockStyle(type) {
        switch (type) {
            case "heading1":
                return "block-h1";
            case "heading2":
                return "block-h2";
            case "heading3":
                return "block-h3";
            case "bulletList":
                return "block-list";
            case "orderedList":
                return "block-list";
            case "taskList":
                return "block-task";
            case "blockquote":
                return "block-quote";
            case "codeBlock":
                return "block-code";
            default:
                return "";
        }
    }

    export function getEditor() {
        return {
            focusEditor: (position) =>
                contentComponent?.focusEditor(position) || false,
        };
    }

    onMount(async () => {
        // Wait for TipTap to be fully initialized in the child component
        await tick();

        setTimeout(() => {
            focusEditor("start");
        }, 200);
    });

    // Watch for changes to autoFocus prop
    $: if (autoFocus && editor) {
        logger.debug("Auto-focus triggered", { blockId: block.id });
        setTimeout(() => {
            focusEditor("end");
        }, 10);
    }
</script>

<div class="block-container {blockStyle}" bind:this={editor}>
    <div class="block-controls">
        <BlockTimestamp createdAt={block.createdAt} />
    </div>

    <div class="block-content">
        <EditorContent
            bind:this={contentComponent}
            {block}
            on:keydown={handleKeyDown}
            on:contentUpdate={handleContentUpdate}
            on:blockTypeChanged={handleBlockTypeChanged}
            on:hashtagClick={handleHashtagClick}
        />
    </div>
</div>

<style>
    .block-container {
        display: flex;
        /* Reduced margin between blocks */
        margin-bottom: 2px;
        border-radius: 5px;
        position: relative;
        transition: background-color 0.2s ease;
    }

    .block-container:hover .block-controls {
        opacity: 1;
    }

    .block-controls {
        display: flex;
        flex-direction: column;
        padding-top: 10px;
        opacity: 0;
        transition: opacity 0.2s ease;
    }

    .block-content {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    /* Block type styles - with reduced margins */
    .block-h1 :global(.editor-content) {
        font-size: 1.875rem;
        font-weight: 700;
        margin-top: 0.5rem; /* Reduced from 1rem */
        margin-bottom: 0.25rem; /* Reduced from 0.5rem */
    }

    .block-h2 :global(.editor-content) {
        font-size: 1.5rem;
        font-weight: 600;
        margin-top: 0.375rem; /* Reduced from 0.75rem */
        margin-bottom: 0.25rem; /* Reduced from 0.5rem */
    }

    .block-h3 :global(.editor-content) {
        font-size: 1.25rem;
        font-weight: 600;
        margin-top: 0.25rem; /* Reduced from 0.5rem */
        margin-bottom: 0.125rem; /* Reduced from 0.25rem */
    }

    .block-quote :global(.editor-content) {
        border-left: 3px solid #e5e7eb;
        padding-left: 1rem;
        font-style: italic;
        color: #4b5563;
    }

    .block-code :global(.editor-content) {
        font-family: monospace;
        background-color: #f3f4f6;
        border-radius: 0.375rem;
        padding: 0.75rem;
    }

    /* Add global styles to override browser defaults */
    :global(.ProseMirror) {
        margin: 0;
    }

    :global(.ProseMirror p) {
        line-height: 1.75; /* Adjust this value as needed (1.5, 2, etc.) */
        margin-bottom: 1em; /* Additional spacing between paragraphs */
    }

    :global(.ProseMirror h1),
    :global(.ProseMirror h2),
    :global(.ProseMirror h3) {
        margin: 0;
    }
</style>
